import React, { ReactNode } from 'react';
import usePrivileges from '../hooks/usePrivileges';

interface PermissionGateProps {
  /** Single privilege slug. Pass null/undefined to always render. */
  slug?: string | null;
  /** Array of slugs, used together with `mode`. */
  slugs?: string[];
  /** 'any' = at least one slug required (default). 'all' = all slugs required. */
  mode?: 'any' | 'all';
  /** What to render when permission is denied. Defaults to null. */
  fallback?: ReactNode;
  children?: ReactNode;
}

/**
 * <PermissionGate> — Conditionally renders children based on privilege slugs.
 *
 * Usage:
 *   <PermissionGate slug="course-management.create">
 *     <button>Create Course</button>
 *   </PermissionGate>
 *
 *   <PermissionGate slugs={['course-management.update', 'course-management.delete']} mode="any">
 *     <ActionMenu />
 *   </PermissionGate>
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  slug = null,
  slugs = [],
  mode = 'any',
  fallback = null,
  children,
}) => {
  const { can, canAny, canAll } = usePrivileges();

  let hasPermission = false;

  if (slug) {
    hasPermission = can(slug);
  } else if (slugs.length) {
    hasPermission = mode === 'all' ? canAll(slugs) : canAny(slugs);
  } else {
    hasPermission = true;
  }

  return hasPermission ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGate;
